<script setup lang="ts">
import {
  AdjustmentsHorizontalIcon,
  BookmarkSquareIcon,
  ChartBarSquareIcon,
  GlobeEuropeAfricaIcon,
  StarIcon,
  TrophyIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline'
import { MARKETING_ROUTES, MARKETING_SCREENSHOTS } from '~~/shared/config/marketing'

definePageMeta({ layout: 'marketing' })

useMarketingSeo({
  title: 'Screener actions, Score et Podium Trident | AppTrident',
  description: 'Filtrez plus de 4 000 entreprises avec le screener AppTrident, le Score Trident et un Podium visuel des meilleures opportunités du moment.',
  path: MARKETING_ROUTES.discover,
})

const featureCards = [
  { icon: AdjustmentsHorizontalIcon, title: 'Des filtres réellement utiles', description: 'Croissance, rentabilité, dette, dilution, dividendes, valorisation, PEA, secteurs, pays et conviction des gérants suivis.' },
  { icon: StarIcon, title: 'Un Score sur 20', description: 'Une lecture synthétique de la croissance, de la rentabilité, de la solidité financière, de la qualité et de la valorisation.' },
  { icon: TrophyIcon, title: 'Un classement visuel', description: 'Le Podium regroupe les entreprises qualifiées en niveaux Or, Argent et Bronze et montre les mouvements récents.' },
  { icon: BookmarkSquareIcon, title: 'Vos recherches sauvegardées', description: 'Enregistrez vos combinaisons de filtres et choisissez les colonnes adaptées à votre manière d’analyser.' },
  { icon: GlobeEuropeAfricaIcon, title: 'Les grands indices', description: 'Explorez le S&P 500, le MSCI World, le Nasdaq 100 et le CAC 40 avec les mêmes critères fondamentaux.' },
  { icon: UserGroupIcon, title: 'Les portefeuilles de gérants', description: 'Utilisez les portefeuilles publics suivis par AppTrident comme filtres et repérez les entreprises détenues par plusieurs gérants.' },
]

const filters = [
  ['Qualité', 'Score et Rang Trident, croissance du chiffre d’affaires, du bénéfice et du free cash flow.'],
  ['Rentabilité', 'ROCE, marge opérationnelle, conversion des bénéfices en trésorerie et historique des marges.'],
  ['Solidité', 'Dette, couverture des intérêts, dépenses d’investissement, rachats d’actions et dilution.'],
  ['Valorisation', 'PER, PEG et hypothèses de valorisation utilisées dans le parcours AppTrident.'],
  ['Contexte', 'Pays, secteur, industrie, capitalisation, PEA, dividendes, indices et portefeuilles suivis.'],
]

const podiumTiers = [
  { icon: '🥇', label: 'Or', color: 'border-amber-200 bg-amber-50', text: 'text-amber-950' },
  { icon: '🥈', label: 'Argent', color: 'border-slate-200 bg-slate-100', text: 'text-slate-900' },
  { icon: '🥉', label: 'Bronze', color: 'border-orange-200 bg-orange-50', text: 'text-orange-950' },
]
</script>

<template>
  <div>
    <MarketingFeatureHero
      eyebrow="Étape 1 · Découvrir"
      title="Repérez les entreprises qui méritent votre attention."
      description="Partez d’un univers de plus de 4 000 entreprises et réduisez-le avec des critères fondamentaux, la méthode Trident et des classements immédiatement lisibles."
      :bullets="['Trouvez des entreprises adaptées à votre stratégie.', 'Comprenez en un regard pourquoi elles sont qualifiées.', 'Revenez facilement à vos recherches et listes préférées.']"
      page="decouvrir"
      preview-type="discover"
      :preview-src="MARKETING_SCREENSHOTS.screener"
      preview-alt="Screener AppTrident avec des filtres fondamentaux et le Score Trident"
      preview-caption="Le Screener rassemble les critères de qualité, de croissance, de solidité et de valorisation dans une seule vue."
    />
    <MarketingTrustStrip page="decouvrir" />

    <section class="bg-white py-20 sm:py-28">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading eyebrow="Un univers organisé" title="Moins de bruit. Plus de candidats qui correspondent à vos critères." description="L'AppTrident ne vous impose pas une liste opaque. L’application vous permet de partir de la méthode Trident, puis d’affiner chaque critère selon votre niveau et votre stratégie." />
        <div class="mt-12 grid gap-x-12 sm:grid-cols-2 lg:grid-cols-3">
          <article v-for="item in featureCards" :key="item.title" class="flex gap-4 border-t border-slate-200 py-6">
            <component :is="item.icon" class="mt-0.5 h-7 w-7 shrink-0 text-sky-blue-700" aria-hidden="true" />
            <div><h2 class="text-lg font-bold text-slate-950">{{ item.title }}</h2><p class="mt-3 text-sm leading-6 text-slate-600">{{ item.description }}</p></div>
          </article>
        </div>
      </div>
    </section>

    <section class="bg-slate-50 py-20 sm:py-28">
      <div class="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <MarketingProductPreview type="discover" :src="MARKETING_SCREENSHOTS.podium" alt="Podium Trident présentant les entreprises classées Or, Argent et Bronze" caption="Le Podium met en avant les entreprises qualifiées, leur niveau de valorisation et leurs mouvements récents." />
        <div>
          <MarketingSectionHeading align="left" eyebrow="Le Podium Trident" title="Les meilleures opportunités remontent à la surface" description="Les entreprises qualifiées sont regroupées par niveau de qualité et de valorisation. Vous voyez aussi les nouvelles entrées, les sorties, les dossiers en attente et les entreprises temporairement mises en pause." />
          <div class="mt-8 grid grid-cols-3 gap-3">
            <div v-for="tier in podiumTiers" :key="tier.label" :class="['rounded-xl border p-4 text-center', tier.color]"><span class="text-2xl">{{ tier.icon }}</span><p :class="['mt-2 text-sm font-bold', tier.text]">{{ tier.label }}</p></div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-slate-950 py-20 sm:py-28">
      <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <MarketingSectionHeading inverse eyebrow="À votre niveau de détail" title="Commencez simplement. Affinez seulement lorsque c’est utile." description="Les filtres sont regroupés par question d’investissement, pour éviter de vous perdre dans une liste d’indicateurs sans contexte." />
        <div class="mt-12 divide-y divide-white/10 rounded-2xl border border-white/10 bg-white/5 px-6 sm:px-8">
          <div v-for="([title, description], index) in filters" :key="title" class="grid gap-3 py-6 sm:grid-cols-[44px_150px_1fr] sm:items-start">
            <span class="text-sm font-bold text-sky-blue-300">0{{ index + 1 }}</span>
            <h3 class="font-bold text-white">{{ title }}</h3>
            <p class="text-sm leading-6 text-slate-300">{{ description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white py-16">
      <div class="mx-auto flex max-w-5xl flex-col items-center justify-between gap-6 px-4 text-center sm:px-6 md:flex-row md:text-left">
        <div><p class="text-sm font-semibold text-sky-blue-700">Étape suivante</p><h2 class="mt-2 text-2xl font-bold text-slate-950">Vous avez trouvé une entreprise.<br/>Est-elle vraiment solide et bien valorisée ?</h2></div>
        <MarketingCta :href="MARKETING_ROUTES.analyze" label="Passer à l’analyse" page="decouvrir" section="next-step" destination="feature" variant="secondary" />
      </div>
    </section>

    <MarketingFinalCta page="decouvrir" title="Réduisez 4 000 entreprises à celles qui méritent votre analyse." description="Accédez au Screener, au Score Trident, au Podium et à tout le parcours AppTrident dans une offre unique." />
  </div>
</template>
